/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import {
    getImagesFromAttribute,
    getImageLabel,
    getRating,
    mapGtmItem
} from '../api/mapping'

const topSearchMapping = {
    /**
     * format product items
     *
     * @param items
     * @returns {*}
     */
    formatProductItems(items) {
        return items.map(item => ({
            name: item.product.name,
            priceRange: item.product.price_range,
            currency: item?.product?.price_range?.maximum_price?.regular_price?.currency ?? '',
            images: getImagesFromAttribute(item),
            imageLabel: getImageLabel(item),
            url: item.product.canonical_url,
            id: item.product.id,
            sku: item.product.sku,
            rating: getRating(item),
            gtmItem: mapGtmItem(item)
        }))
    },
    /**
     * format product items
     *
     * @param items
     * @returns {*}
     */
    formatContextProductItems(items) {
        return items.map((item, key) => ({
            name: item.product.name,
            sku: item.product.sku,
            url: item.product.canonical_url,
            imageUrl: this.getContextImageUrl(item),
            price: item?.product?.price_range?.minimum_price?.final_price?.value ?? 0,
            rank: key + 1
        }))
    },
    /**
     * format suggestions
     *
     * @param suggestions
     * @returns {*}
     */
    formatContextSuggestions(suggestions) {
        return suggestions.map((item, key) => ({
            suggestion: item,
            rank: key + 1
        }))
    },
    /**
     * format facets
     *
     * @param facets
     * @returns {*}
     */
    formatContextFacets(facets) {
        return facets
            .filter((facet) => typeof facet?.attribute === 'string' && facet.attribute.trim() !== '')
            .map((facet) => ({
                attribute: facet.attribute,
                title: facet.title || facet.attribute,
                type: facet.type || 'PINNED',
                buckets: this.formatFacetBuckets(facet.buckets)
            }))
    },

    /**
     * Resolve product image URL for SearchResultProduct context.
     *
     * @param {Object} item
     * @returns {string}
     */
    getContextImageUrl(item) {
        const images = getImagesFromAttribute(item)

        return images?.small?.url ||
            images?.large?.url ||
            images?.thumbnail?.url ||
            images?.fallback?.url ||
            ''
    },

    /**
     * Normalize facet buckets to Adobe schema shape.
     *
     * @param {Array|undefined} buckets
     * @returns {Array}
     */
    formatFacetBuckets(buckets) {
        if (!Array.isArray(buckets)) {
            return []
        }

        const toNumber = (value) => {
            const parsed = Number(value)
            return Number.isFinite(parsed) ? parsed : null
        }

        return buckets.map((bucket) => {
            const normalizedBucket = {
                title: this.getFacetBucketTitle(bucket)
            }

            const count = toNumber(bucket?.count)
            const from = toNumber(bucket?.from)
            const to = toNumber(bucket?.to)
            const min = toNumber(bucket?.min)
            const max = toNumber(bucket?.max)

            if (count !== null) {
                normalizedBucket.count = count
            }

            if (from !== null) {
                normalizedBucket.from = from
            }

            if (to !== null) {
                normalizedBucket.to = to
            }

            if (min !== null) {
                normalizedBucket.min = min
            }

            if (max !== null) {
                normalizedBucket.max = max
            }

            if (normalizedBucket.count === undefined &&
                normalizedBucket.min === undefined &&
                normalizedBucket.max === undefined) {
                normalizedBucket.count = 0
            }

            return normalizedBucket
        })
    },

    /**
     * Resolve bucket title from scalar/range/category bucket shapes.
     *
     * @param {Object} bucket
     * @returns {string}
     */
    getFacetBucketTitle(bucket) {
        if (typeof bucket?.title === 'string' && bucket.title.trim() !== '') {
            return bucket.title
        }

        if (typeof bucket?.name === 'string' && bucket.name.trim() !== '') {
            return bucket.name
        }

        if (typeof bucket?.value === 'string' && bucket.value.trim() !== '') {
            return bucket.value
        }

        if (typeof bucket?.path === 'string' && bucket.path.trim() !== '') {
            return bucket.path
        }

        if (bucket?.from !== undefined || bucket?.to !== undefined) {
            const from = bucket?.from ?? ''
            const to = bucket?.to ?? ''
            const rangeLabel = `${from}-${to}`.replace(/^-|-$/g, '').trim()

            if (rangeLabel !== '') {
                return rangeLabel
            }
        }

        if (bucket?.min !== undefined || bucket?.max !== undefined) {
            const min = bucket?.min ?? ''
            const max = bucket?.max ?? ''
            const statsLabel = `${min}-${max}`.replace(/^-|-$/g, '').trim()

            if (statsLabel !== '') {
                return statsLabel
            }
        }

        return 'unknown'
    }
}

export default topSearchMapping
